import React, { useState, useEffect } from 'react';
import { Plus, Trash2, ChevronRight, Building2, Layers, LayoutGrid } from 'lucide-react';
import { createHostel, getHostels, deleteHostel, createBlock, getBlocks, deleteBlock, createFloor, getFloors, deleteFloor, createRoom, getRooms } from '../../../services/hostelService';
import toast from 'react-hot-toast';

const ROOM_TYPES = ['Single Sharing', 'Double Sharing', 'Triple Sharing', 'Four Sharing'];
const HOSTEL_TYPES = ['Boys', 'Girls', 'Co-Ed'];

const HostelSetup = ({ coachingId }) => {
  const [hostels, setHostels] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [floors, setFloors] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedHostel, setSelectedHostel] = useState(null);
  const [selectedBlock, setSelectedBlock] = useState(null);
  const [selectedFloor, setSelectedFloor] = useState(null);

  // Forms
  const [showHostelForm, setShowHostelForm] = useState(false);
  const [showBlockForm, setShowBlockForm] = useState(false);
  const [showFloorForm, setShowFloorForm] = useState(false);
  const [showRoomForm, setShowRoomForm] = useState(false);

  const [hostelForm, setHostelForm] = useState({ name: '', type: 'Boys', address: '', contactNumber: '', description: '' });
  const [blockName, setBlockName] = useState('');
  const [floorName, setFloorName] = useState('');
  const [roomForm, setRoomForm] = useState({ roomNumber: '', capacity: 2, roomType: 'Double Sharing' });

  useEffect(() => { loadHostels(); }, [coachingId]);
  useEffect(() => { if (selectedHostel) loadBlocks(); }, [selectedHostel]);
  useEffect(() => { if (selectedBlock) loadFloors(); }, [selectedBlock]);
  useEffect(() => { if (selectedFloor) loadRooms(); }, [selectedFloor]);

  const loadHostels = async () => {
    setLoading(true);
    const data = await getHostels(coachingId);
    setHostels(data);
    setLoading(false);
  };

  const loadBlocks = async () => {
    const data = await getBlocks(selectedHostel.id);
    setBlocks(data);
    setSelectedBlock(null); setFloors([]); setRooms([]);
  };

  const loadFloors = async () => {
    const data = await getFloors(selectedBlock.id);
    setFloors(data);
    setSelectedFloor(null); setRooms([]);
  };

  const loadRooms = async () => {
    const data = await getRooms(selectedFloor.id);
    setRooms(data);
  };

  const handleCreateHostel = async (e) => {
    e.preventDefault();
    if (!hostelForm.name.trim()) return toast.error('Hostel name required');
    await createHostel(coachingId, hostelForm);
    toast.success('Hostel created');
    setShowHostelForm(false);
    setHostelForm({ name: '', type: 'Boys', address: '', contactNumber: '', description: '' });
    loadHostels();
  };

  const handleCreateBlock = async (e) => {
    e.preventDefault();
    if (!blockName.trim()) return toast.error('Block name required');
    await createBlock(coachingId, selectedHostel.id, { name: blockName });
    toast.success('Block created');
    setShowBlockForm(false); setBlockName('');
    loadBlocks();
  };

  const handleCreateFloor = async (e) => {
    e.preventDefault();
    if (!floorName.trim()) return toast.error('Floor name required');
    await createFloor(coachingId, selectedHostel.id, selectedBlock.id, { name: floorName });
    toast.success('Floor created');
    setShowFloorForm(false); setFloorName('');
    loadFloors();
  };

  const handleCreateRoom = async (e) => {
    e.preventDefault();
    if (!roomForm.roomNumber.trim()) return toast.error('Room number required');
    await createRoom(coachingId, selectedHostel.id, selectedBlock.id, selectedFloor.id, roomForm);
    toast.success(`Room created with ${roomForm.capacity} beds`);
    setShowRoomForm(false); setRoomForm({ roomNumber: '', capacity: 2, roomType: 'Double Sharing' });
    loadRooms();
  };

  const handleDeleteHostel = async (id) => {
    if (!window.confirm('Delete this hostel?')) return;
    await deleteHostel(id);
    toast.success('Hostel deleted');
    setSelectedHostel(null); setBlocks([]); setFloors([]); setRooms([]);
    loadHostels();
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>;

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-gray-500 flex-wrap">
        <button onClick={() => { setSelectedHostel(null); setSelectedBlock(null); setSelectedFloor(null); }} className="hover:text-blue-600 font-medium">All Hostels</button>
        {selectedHostel && <><ChevronRight size={14} /><button onClick={() => { setSelectedBlock(null); setSelectedFloor(null); }} className="hover:text-blue-600">{selectedHostel.name}</button></>}
        {selectedBlock && <><ChevronRight size={14} /><button onClick={() => setSelectedFloor(null)} className="hover:text-blue-600">{selectedBlock.name}</button></>}
        {selectedFloor && <><ChevronRight size={14} /><span>{selectedFloor.name}</span></>}
      </div>

      {/* Level: Hostels */}
      {!selectedHostel && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-800 dark:text-white">Hostels</h2>
            <button onClick={() => setShowHostelForm(true)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
              <Plus size={16} /> Add Hostel
            </button>
          </div>

          {showHostelForm && (
            <form onSubmit={handleCreateHostel} className="bg-white dark:bg-gray-800 border rounded-xl p-4 mb-4 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <input value={hostelForm.name} onChange={e => setHostelForm({...hostelForm, name: e.target.value})} placeholder="Hostel Name *" className="border rounded-lg px-3 py-2 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                <select value={hostelForm.type} onChange={e => setHostelForm({...hostelForm, type: e.target.value})} className="border rounded-lg px-3 py-2 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white">
                  {HOSTEL_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <input value={hostelForm.address} onChange={e => setHostelForm({...hostelForm, address: e.target.value})} placeholder="Address" className="border rounded-lg px-3 py-2 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                <input value={hostelForm.contactNumber} onChange={e => setHostelForm({...hostelForm, contactNumber: e.target.value})} placeholder="Contact Number" className="border rounded-lg px-3 py-2 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
              </div>
              <textarea value={hostelForm.description} onChange={e => setHostelForm({...hostelForm, description: e.target.value})} placeholder="Description" rows={2} className="w-full border rounded-lg px-3 py-2 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
              <div className="flex gap-2">
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm">Create</button>
                <button type="button" onClick={() => setShowHostelForm(false)} className="px-4 py-2 border rounded-lg text-sm">Cancel</button>
              </div>
            </form>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {hostels.map(h => (
              <div key={h.id} onClick={() => setSelectedHostel(h)} className="bg-white dark:bg-gray-800 border rounded-xl p-4 cursor-pointer hover:border-blue-400 hover:shadow-md transition-all">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-800 dark:text-white">{h.name}</h3>
                    <p className="text-xs text-gray-500 mt-1">{h.type} • {h.address || 'No address'}</p>
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); handleDeleteHostel(h.id); }} className="text-red-400 hover:text-red-600 p-1"><Trash2 size={16} /></button>
                </div>
              </div>
            ))}
            {hostels.length === 0 && <p className="text-gray-400 text-sm col-span-full text-center py-8">No hostels created yet. Click "Add Hostel" to start.</p>}
          </div>
        </div>
      )}

      {/* Level: Blocks */}
      {selectedHostel && !selectedBlock && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-800 dark:text-white">Blocks in {selectedHostel.name}</h2>
            <button onClick={() => setShowBlockForm(true)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
              <Plus size={16} /> Add Block
            </button>
          </div>

          {showBlockForm && (
            <form onSubmit={handleCreateBlock} className="bg-white dark:bg-gray-800 border rounded-xl p-4 mb-4 flex gap-3">
              <input value={blockName} onChange={e => setBlockName(e.target.value)} placeholder="Block Name (e.g. Block A)" className="flex-1 border rounded-lg px-3 py-2 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
              <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm">Create</button>
              <button type="button" onClick={() => setShowBlockForm(false)} className="px-4 py-2 border rounded-lg text-sm">Cancel</button>
            </form>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {blocks.map(b => (
              <div key={b.id} onClick={() => setSelectedBlock(b)} className="bg-white dark:bg-gray-800 border rounded-xl p-4 cursor-pointer hover:border-blue-400 hover:shadow-md transition-all flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Layers size={20} className="text-blue-500" />
                  <span className="font-medium text-gray-800 dark:text-white">{b.name}</span>
                </div>
                <button onClick={(e) => { e.stopPropagation(); deleteBlock(b.id).then(loadBlocks); toast.success('Deleted'); }} className="text-red-400 hover:text-red-600 p-1"><Trash2 size={14} /></button>
              </div>
            ))}
            {blocks.length === 0 && <p className="text-gray-400 text-sm col-span-full text-center py-8">No blocks yet.</p>}
          </div>
        </div>
      )}

      {/* Level: Floors */}
      {selectedBlock && !selectedFloor && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-800 dark:text-white">Floors in {selectedBlock.name}</h2>
            <button onClick={() => setShowFloorForm(true)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
              <Plus size={16} /> Add Floor
            </button>
          </div>

          {showFloorForm && (
            <form onSubmit={handleCreateFloor} className="bg-white dark:bg-gray-800 border rounded-xl p-4 mb-4 flex gap-3">
              <input value={floorName} onChange={e => setFloorName(e.target.value)} placeholder="Floor Name (e.g. Floor 1)" className="flex-1 border rounded-lg px-3 py-2 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
              <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm">Create</button>
              <button type="button" onClick={() => setShowFloorForm(false)} className="px-4 py-2 border rounded-lg text-sm">Cancel</button>
            </form>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {floors.map(f => (
              <div key={f.id} onClick={() => setSelectedFloor(f)} className="bg-white dark:bg-gray-800 border rounded-xl p-4 cursor-pointer hover:border-blue-400 hover:shadow-md transition-all flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <LayoutGrid size={20} className="text-green-500" />
                  <span className="font-medium text-gray-800 dark:text-white">{f.name}</span>
                </div>
                <button onClick={(e) => { e.stopPropagation(); deleteFloor(f.id).then(loadFloors); toast.success('Deleted'); }} className="text-red-400 hover:text-red-600 p-1"><Trash2 size={14} /></button>
              </div>
            ))}
            {floors.length === 0 && <p className="text-gray-400 text-sm col-span-full text-center py-8">No floors yet.</p>}
          </div>
        </div>
      )}

      {/* Level: Rooms */}
      {selectedFloor && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-800 dark:text-white">Rooms on {selectedFloor.name}</h2>
            <button onClick={() => setShowRoomForm(true)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
              <Plus size={16} /> Add Room
            </button>
          </div>

          {showRoomForm && (
            <form onSubmit={handleCreateRoom} className="bg-white dark:bg-gray-800 border rounded-xl p-4 mb-4 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <input value={roomForm.roomNumber} onChange={e => setRoomForm({...roomForm, roomNumber: e.target.value})} placeholder="Room Number (e.g. A101)" className="border rounded-lg px-3 py-2 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                <select value={roomForm.roomType} onChange={e => {
                  const type = e.target.value;
                  const cap = type === 'Single Sharing' ? 1 : type === 'Double Sharing' ? 2 : type === 'Triple Sharing' ? 3 : 4;
                  setRoomForm({...roomForm, roomType: type, capacity: cap});
                }} className="border rounded-lg px-3 py-2 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white">
                  {ROOM_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <input type="number" value={roomForm.capacity} onChange={e => setRoomForm({...roomForm, capacity: parseInt(e.target.value) || 1})} min={1} max={10} className="border rounded-lg px-3 py-2 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
              </div>
              <div className="flex gap-2">
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm">Create Room</button>
                <button type="button" onClick={() => setShowRoomForm(false)} className="px-4 py-2 border rounded-lg text-sm">Cancel</button>
              </div>
            </form>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {rooms.map(r => (
              <div key={r.id} className="bg-white dark:bg-gray-800 border rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-gray-800 dark:text-white">Room {r.roomNumber}</h3>
                  <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full">{r.roomType}</span>
                </div>
                <div className="flex items-center gap-4 text-sm text-gray-500">
                  <span>Capacity: {r.capacity}</span>
                  <span>Occupied: {r.occupiedBeds || 0}</span>
                  <span className="text-green-600">Vacant: {r.capacity - (r.occupiedBeds || 0)}</span>
                </div>
              </div>
            ))}
            {rooms.length === 0 && <p className="text-gray-400 text-sm col-span-full text-center py-8">No rooms yet. Beds are auto-generated when you create a room.</p>}
          </div>
        </div>
      )}
    </div>
  );
};

export default HostelSetup;
